import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import {
  FloatingActionFabComponent,
  ListFilterShortcutDirective,
  PageContextGuardComponent,
} from '@clinic-platform/ui-components';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { ClinicQuickViewModalComponent } from '../../clinics/components/clinic-quick-view-modal.component';
import {
  ClinicDetailsFormValue,
  ClinicDetailsModalComponent,
} from './clinic-details-modal.component';
import {
  ClinicOperationalSettingsFormValue,
  ClinicOperationalSettingsModalComponent,
} from './clinic-operational-settings-modal.component';
import {
  ClinicSettingsFiltersModalComponent,
  ClinicSettingsFilterState,
  getDefaultClinicSettingsFilters,
} from './clinic-settings-filters-modal.component';
import { ClinicSettingsListComponent } from './clinic-settings-list.component';
import {
  ClinicOperationalSettingsDto,
  ClinicProcedureAssignmentDto,
  ClinicSaveOutcome,
  SaveClinicSettingsRequest,
} from '../data-access/settings.models';
import { SettingsService } from '../data-access/settings.service';
import { OperationsSettingsStateService } from './operations-settings-state.service';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { SettingsRequestStateBase } from './settings-request-state.base';
import { ClinicSaveDraftStore } from '../data-access/clinic-save-draft.store';

@Component({
  selector: 'app-clinic-settings-panel',
  standalone: true,
  imports: [
    ClinicDetailsModalComponent,
    ClinicOperationalSettingsModalComponent,
    ClinicQuickViewModalComponent,
    ClinicSettingsFiltersModalComponent,
    ClinicSettingsListComponent,
    CompanyContextRequiredComponent,
    FloatingActionFabComponent,
    ListFilterShortcutDirective,
    PageContextGuardComponent,
    TranslatePipe,
  ],
  template: `
    <section
      libListFilterShortcut
      [libListFilterShortcut]="hasRequiredCompanyContext()"
      (libListFilterShortcutOpen)="openClinicFiltersModal()"
    >
    @if (isClinicsMode) {
      @if (clinicActionErrorText) {
        <div class="settings-page__error">{{ clinicActionErrorText | t }}</div>
      }

      @if (clinicActionSuccessText) {
        <div class="settings-page__success">{{ clinicActionSuccessText }}</div>
      }

      <lib-page-context-guard [blocked]="isCompanyContextRequiredError(clinicSettingsErrorText)">
        <app-company-context-required [message]="clinicSettingsErrorText || ''" />
      </lib-page-context-guard>

      @if (!isCompanyContextRequiredError(clinicSettingsErrorText)) {
        <app-clinic-settings-list
          [items]="visibleClinicSettings"
          [totalCount]="clinicSettings.length"
          [loading]="isClinicSettingsLoading"
          [errorText]="clinicSettingsErrorText"
          [clinicLabelPlural]="clinicLabelPlural"
          [clinicLabelPluralLower]="clinicLabelPluralLower"
          (retry)="loadClinicSettings()"
          (view)="openClinicQuickView($event)"
          (settings)="openClinicSettingsModal($event)"
          (edit)="openClinicEditModal($event)"
          (delete)="deleteClinic($event)"
        />
      }

      <ng-template #clinicPageActionsMenu let-close="close">
        <button
          type="button"
          role="menuitem"
          class="action-menu__item"
          [disabled]="isCreatingClinic || !hasRequiredCompanyContext()"
          (click)="openClinicCreateModal(); close()"
        >
          Create {{ clinicLabelLower }}
        </button>
        <button type="button" role="menuitem" class="action-menu__item" (click)="openClinicFiltersModal(); close()">
          Filters & Sort (Ctrl+F)
        </button>
        @if (hasActiveClinicFilters) {
          <button type="button" role="menuitem" class="action-menu__item" (click)="resetClinicFilters(); close()">
            Reset
          </button>
        }
        <button type="button" role="menuitem" class="action-menu__item" (click)="loadClinicSettings(); close()">
          Refresh
        </button>
      </ng-template>

      <lib-floating-action-fab
        [menuTemplate]="clinicPageActionsMenu"
        buttonAriaLabel="Open clinic actions"
        [disabled]="!hasRequiredCompanyContext()"
      />
    }

    <app-clinic-settings-filters-modal
      [isOpen]="isClinicFiltersModalOpen"
      [clinicLabelPluralLower]="clinicLabelPluralLower"
      [filters]="appliedClinicFilters"
      (close)="closeClinicFiltersModal()"
      (apply)="applyClinicFilters($event)"
      (reset)="resetClinicFilters()"
    />

    <app-clinic-details-modal
      [isOpen]="isClinicCreateModalOpen"
      mode="create"
      [clinicLabel]="clinicLabel"
      [clinicLabelLower]="clinicLabelLower"
      [isSaving]="isCreatingClinic"
      (close)="closeClinicCreateModal()"
      (save)="createClinic($event)"
    />

    @if (selectedClinicQuickViewId) {
      <app-clinic-quick-view-modal
        [isOpen]="true"
        [clinicId]="selectedClinicQuickViewId"
        [companyIdOverride]="clinicQuickViewCompanyId"
        title="Clinic Quick View"
        (close)="closeClinicQuickView()"
      />
    }

    <app-clinic-details-modal
      [isOpen]="isClinicEditModalOpen && !!editingClinic"
      mode="edit"
      [clinicLabel]="clinicLabel"
      [clinicLabelLower]="clinicLabelLower"
      [isSaving]="isSavingClinicEdit"
      [value]="editingClinicDetailsValue"
      (close)="closeClinicEditModal()"
      (save)="saveClinicEditModal($event)"
    />

    <app-clinic-operational-settings-modal
      [isOpen]="isClinicSettingsModalOpen"
      [clinic]="editingClinicSettings"
      [clinicLabel]="clinicLabel"
      [procedureTypeLabelPluralLower]="procedureTypeLabelPluralLower"
      [procedureAssignments]="clinicProcedureAssignments"
      [isAssignmentsLoading]="isClinicProcedureAssignmentsLoading"
      [assignmentsReady]="clinicProcedureAssignmentsReady"
      [isSaving]="isSavingClinicSettings"
      [pendingSave]="pendingClinicSave"
      [saveError]="clinicSettingsModalError"
      [saveBlocked]="clinicSaveBlocked"
      [defaultTimeZoneRegion]="operationsState.selectedTimeZoneRegion"
      (close)="closeClinicSettingsModal()"
      (save)="saveClinicSettingsModal($event)"
      (recover)="recoverClinicSave()"
      (retrySave)="retryClinicSave()"
    />
    </section>`,
  styles: [`
    :host {
      display: block;
    }

    section {
      display: contents;
    }

    .settings-page__error {
      margin-top: 12px;
      padding: 10px 12px;
      border: 1px solid #fecaca;
      border-radius: 8px;
      background: #fef2f2;
      color: #b91c1c;
      font-size: 14px;
    }

    .settings-page__success {
      margin-top: 12px;
      padding: 10px 12px;
      border: 1px solid #bbf7d0;
      border-radius: 8px;
      background: #f0fdf4;
      color: #166534;
      font-size: 14px;
    }

  `],
})
export class ClinicSettingsPanelComponent extends SettingsRequestStateBase implements OnInit, OnChanges, OnDestroy
{
  private static readonly COMPANY_CONTEXT_REQUIRED_ERROR_KEY = 'error.settingsCompanyContextRequired';

  @Input() public companyId: string | undefined;
  @Input() public hasCompanyContext = true;

  private readonly settingsService = inject(SettingsService);
  private readonly clinicSaveDrafts = inject(ClinicSaveDraftStore);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  protected readonly operationsState = inject(OperationsSettingsStateService);
  protected readonly isClinicsMode = true;

  protected clinicSettings: ClinicOperationalSettingsDto[] = [];
  protected visibleClinicSettings: ClinicOperationalSettingsDto[] = [];
  protected clinicSettingsErrorText: string | null = null;
  protected isClinicSettingsLoading = false;
  protected isCreatingClinic = false;
  protected isClinicCreateModalOpen = false;
  protected isClinicFiltersModalOpen = false;
  protected clinicActionErrorText: string | null = null;
  protected clinicActionSuccessText: string | null = null;
  protected isClinicEditModalOpen = false;
  protected editingClinic: ClinicOperationalSettingsDto | null = null;
  protected editingClinicDetailsValue: ClinicDetailsFormValue | null = null;
  protected isSavingClinicEdit = false;
  protected isClinicSettingsModalOpen = false;
  protected editingClinicSettings: ClinicOperationalSettingsDto | null = null;
  protected isSavingClinicSettings = false;
  protected pendingClinicSave: SaveClinicSettingsRequest | null = null;
  protected clinicSaveError: string | null = null;
  protected clinicSaveBlocked = false;
  protected get clinicSettingsModalError(): string | null {
    return this.clinicSaveError || this.clinicActionErrorText;
  }
  protected clinicProcedureAssignments: ClinicProcedureAssignmentDto[] = [];
  protected isClinicProcedureAssignmentsLoading = false;
  protected clinicProcedureAssignmentsReady = false;
  private clinicListRequest: Subscription | undefined;
  private assignmentsRequest: Subscription | undefined;
  protected selectedClinicQuickViewId: string | null = null;
  protected appliedClinicFilters: ClinicSettingsFilterState = getDefaultClinicSettingsFilters();

  public ngOnInit(): void
  {
    this.loadByCurrentContext();
  }

  public ngOnChanges(changes: SimpleChanges): void
  {
    if ((changes['companyId'] || changes['hasCompanyContext']) && !changes['companyId']?.firstChange) {
      this.loadByCurrentContext();
    }
  }

  protected get clinicLabel(): string {
    return this.normalizeLabel(
      this.operationsState.form.controls.clinicLabel.value,
      OperationsSettingsStateService.DEFAULT_CLINIC_LABEL,
    );
  }

  protected get clinicLabelLower(): string {
    return this.lowerFirst(this.clinicLabel);
  }

  protected get clinicLabelPlural(): string {
    return this.toPlural(this.clinicLabel);
  }

  protected get clinicLabelPluralLower(): string {
    return this.lowerFirst(this.clinicLabelPlural);
  }

  protected get procedureTypeLabel(): string {
    return this.normalizeLabel(
      this.operationsState.form.controls.procedureTypeLabel.value,
      OperationsSettingsStateService.DEFAULT_PROCEDURE_TYPE_LABEL,
    );
  }

  protected get procedureTypeLabelPluralLower(): string {
    return this.lowerFirst(this.toPlural(this.procedureTypeLabel));
  }

  protected get hasActiveClinicFilters(): boolean {
    const defaults = getDefaultClinicSettingsFilters();
    return this.appliedClinicFilters.query !== defaults.query
      || this.appliedClinicFilters.timeZoneMode !== defaults.timeZoneMode
      || this.appliedClinicFilters.slotOverride !== defaults.slotOverride
      || this.appliedClinicFilters.sort !== defaults.sort;
  }

  protected get clinicQuickViewCompanyId(): string | null {
    return this.getTargetCompanyId() ?? null;
  }

  private loadByCurrentContext(): void
  {
    this.invalidateSettings();
    this.closeClinicSettingsModal();
    this.closeClinicEditModal();
    this.isCreatingClinic = false;
    this.isClinicCreateModalOpen = false;
    this.isClinicFiltersModalOpen = false;
    this.selectedClinicQuickViewId = null;
    this.clinicActionErrorText = null;
    this.clinicActionSuccessText = null;
    this.clinicSettings = [];
    this.visibleClinicSettings = [];
    this.loadOperations();
    if (!this.hasRequiredCompanyContext()) {
      this.applyMissingCompanyContextState();
      return;
    }

    this.loadClinicSettings();
  }

  protected hasRequiredCompanyContext(): boolean {
    return this.hasCompanyContext;
  }

  private applyMissingCompanyContextState(): void {
    this.clinicSettingsErrorText = ClinicSettingsPanelComponent.COMPANY_CONTEXT_REQUIRED_ERROR_KEY;
    this.clinicSettings = [];
    this.visibleClinicSettings = [];
    this.isClinicSettingsLoading = false;
  }

  private getTargetCompanyId(): string | undefined
  {
    const companyId = this.companyId?.trim() ?? '';
    return companyId || undefined;
  }

  protected createClinic(value: ClinicDetailsFormValue): void
  {
    if (!this.hasRequiredCompanyContext()) {
      this.clinicActionErrorText = ClinicSettingsPanelComponent.COMPANY_CONTEXT_REQUIRED_ERROR_KEY;
      this.clinicActionSuccessText = null;
      return;
    }

    if (!value.name || this.isCreatingClinic) {
      return;
    }

    this.isCreatingClinic = true;
    this.clinicActionErrorText = null;
    this.clinicActionSuccessText = null;

    this.currentSettingsRequest(this.settingsService.createClinic({
      name: value.name,
      city: value.city,
      address: value.address,
    }, this.getTargetCompanyId())).subscribe({
      next: () => {
        this.isCreatingClinic = false;
        this.clinicActionSuccessText = `${this.clinicLabel} created`;
        this.isClinicCreateModalOpen = false;
        this.loadClinicSettings();
        this.systemAdminContext.triggerRefresh();
      },
      error: (error: unknown) => {
        this.isCreatingClinic = false;
        this.clinicActionErrorText = resolveHttpErrorMessageKey(error, { fallback: 'error.generic' });
      },
    });
  }

  protected openClinicCreateModal(): void {
    if (!this.hasRequiredCompanyContext()) {
      this.clinicActionErrorText = ClinicSettingsPanelComponent.COMPANY_CONTEXT_REQUIRED_ERROR_KEY;
      this.clinicActionSuccessText = null;
      return;
    }

    this.clinicActionErrorText = null;
    this.clinicActionSuccessText = null;
    this.isClinicCreateModalOpen = true;
  }

  protected closeClinicCreateModal(): void {
    if (this.isCreatingClinic) {
      return;
    }
    this.isClinicCreateModalOpen = false;
  }

  protected openClinicFiltersModal(): void {
    this.isClinicFiltersModalOpen = true;
  }

  protected closeClinicFiltersModal(): void {
    this.isClinicFiltersModalOpen = false;
  }

  protected applyClinicFilters(filters: ClinicSettingsFilterState): void {
    this.appliedClinicFilters = filters;
    this.applyClinicListFilters();
    this.isClinicFiltersModalOpen = false;
  }

  protected resetClinicFilters(): void {
    const defaults = getDefaultClinicSettingsFilters();
    this.appliedClinicFilters = defaults;
    this.applyClinicListFilters();
    this.isClinicFiltersModalOpen = false;
  }

  protected openClinicEditModal(item: ClinicOperationalSettingsDto): void
  {
    this.editingClinic = item;
    this.editingClinicDetailsValue = {
      name: item.clinicName,
      city: item.city ?? '',
      address: item.address ?? '',
    };
    this.isClinicEditModalOpen = true;
    this.clinicActionErrorText = null;
    this.clinicActionSuccessText = null;
  }

  protected closeClinicEditModal(): void
  {
    this.isClinicEditModalOpen = false;
    this.editingClinic = null;
    this.editingClinicDetailsValue = null;
    this.isSavingClinicEdit = false;
  }

  protected saveClinicEditModal(value: ClinicDetailsFormValue): void
  {
    if (!this.editingClinic || !value.name || this.isSavingClinicEdit) {
      return;
    }

    this.isSavingClinicEdit = true;
    this.clinicActionErrorText = null;
    this.clinicActionSuccessText = null;

    this.currentSettingsRequest(this.settingsService.updateClinic(this.editingClinic.clinicId, {
      name: value.name,
      city: value.city,
      address: value.address,
    }, this.getTargetCompanyId())).subscribe({
      next: () => {
        this.isSavingClinicEdit = false;
        this.closeClinicEditModal();
        this.clinicActionSuccessText = `${this.clinicLabel} "${value.name}" updated`;
        this.loadClinicSettings();
        this.systemAdminContext.triggerRefresh();
      },
      error: (error: unknown) => {
        this.isSavingClinicEdit = false;
        this.clinicActionErrorText = resolveHttpErrorMessageKey(error, { fallback: 'error.generic' });
      },
    });
  }

  protected deleteClinic(item: ClinicOperationalSettingsDto): void
  {
    const confirmName = window.prompt(`Type ${this.clinicLabelLower} name to delete: ${item.clinicName}`, '');
    if (confirmName === null) {
      return;
    }

    if (confirmName.trim().toLowerCase() !== item.clinicName.trim().toLowerCase()) {
      this.clinicActionErrorText = 'error.generic';
      return;
    }

    this.clinicActionErrorText = null;
    this.clinicActionSuccessText = null;

    this.currentSettingsRequest(this.settingsService.deleteClinic(item.clinicId, this.getTargetCompanyId())).subscribe({
      next: () => {
        this.clinicActionSuccessText = `${this.clinicLabel} "${item.clinicName}" deleted`;
        this.loadClinicSettings();
        this.systemAdminContext.triggerRefresh();
      },
      error: (error: unknown) => {
        this.clinicActionErrorText = resolveHttpErrorMessageKey(error, { fallback: 'error.generic' });
      },
    });
  }

  protected openClinicQuickView(item: ClinicOperationalSettingsDto): void {
    this.selectedClinicQuickViewId = item.clinicId;
  }

  protected closeClinicQuickView(): void {
    this.selectedClinicQuickViewId = null;
  }

  protected openClinicSettingsModal(item: ClinicOperationalSettingsDto): void
  {
    this.closeClinicSettingsModal();
    this.editingClinicSettings = item;
    this.clinicSaveError = null;
    try {
      this.pendingClinicSave = this.clinicSaveDrafts.load(item.clinicId, this.getTargetCompanyId());
    } catch {
      this.clinicSaveBlocked = true;
      this.clinicSaveError = 'Pending save could not be read. Restore session storage before saving.';
    }
    this.isClinicSettingsModalOpen = true;
    this.clinicActionErrorText = null;
    this.clinicActionSuccessText = null;
    this.clinicProcedureAssignments = [];
    this.isClinicProcedureAssignmentsLoading = true;

    this.assignmentsRequest = this.currentSettingsRequest(this.settingsService
      .getClinicProcedureAssignments(item.clinicId, this.getTargetCompanyId()))
      .subscribe({
        next: (items) => {
          this.clinicProcedureAssignments = items;
          this.clinicProcedureAssignmentsReady = true;
          this.isClinicProcedureAssignmentsLoading = false;
        },
        error: (error: unknown) => {
          this.isClinicProcedureAssignmentsLoading = false;
          this.clinicActionErrorText = resolveHttpErrorMessageKey(error, { fallback: 'error.generic' });
        },
      });
  }

  protected closeClinicSettingsModal(): void
  {
    this.assignmentsRequest?.unsubscribe();
    this.isClinicSettingsModalOpen = false;
    this.editingClinicSettings = null;
    this.isSavingClinicSettings = false;
    this.clinicProcedureAssignments = [];
    this.isClinicProcedureAssignmentsLoading = false;
    this.clinicProcedureAssignmentsReady = false;
    this.pendingClinicSave = null;
    this.clinicSaveError = null;
    this.clinicSaveBlocked = false;
  }

  protected saveClinicSettingsModal(value: ClinicOperationalSettingsFormValue): void
  {
    if (!this.editingClinicSettings || this.isSavingClinicSettings || !this.clinicProcedureAssignmentsReady || this.pendingClinicSave || this.clinicSaveBlocked) {
      return;
    }

    const request: SaveClinicSettingsRequest = { ...value, procedureTypeIds: [...value.procedureTypeIds], operationId: crypto.randomUUID() };
    try {
      this.clinicSaveDrafts.remember(this.editingClinicSettings.clinicId, this.getTargetCompanyId(), request);
    } catch {
      this.clinicSaveBlocked = true;
      this.clinicSaveError = 'Save could not be preserved for recovery. Restore session storage and reopen this form.';
      return;
    }
    this.pendingClinicSave = request;
    this.observeClinicSave(this.settingsService.saveClinicSettings(this.editingClinicSettings.clinicId, request, this.getTargetCompanyId()), true);
  }

  protected retryClinicSave(): void {
    if (!this.editingClinicSettings || !this.pendingClinicSave || this.isSavingClinicSettings) return;
    this.observeClinicSave(this.settingsService.saveClinicSettings(this.editingClinicSettings.clinicId, this.pendingClinicSave, this.getTargetCompanyId()));
  }

  protected recoverClinicSave(): void {
    if (!this.editingClinicSettings || !this.pendingClinicSave || this.isSavingClinicSettings) return;
    this.observeClinicSave(this.settingsService.getClinicSaveOutcome(this.editingClinicSettings.clinicId, this.pendingClinicSave.operationId, this.getTargetCompanyId()));
  }

  private observeClinicSave(request: Observable<ClinicSaveOutcome>, firstAttempt = false): void {
    const clinic = this.editingClinicSettings!;
    const companyId = this.getTargetCompanyId();
    this.isSavingClinicSettings = true;
    this.clinicSaveError = null;
    this.currentSettingsRequest(request).subscribe({
      next: (result) => {
        this.isSavingClinicSettings = false;
        if (result.outcome === 'unknown') {
          this.clinicSaveError = 'Save outcome is not recorded yet. Check again or retry the same save.';
          return;
        }
        try {
          this.clinicSaveDrafts.clear(clinic.clinicId, companyId);
        } catch {
          this.clinicSaveError = 'Save committed, but the recovery record could not be cleared. Check again after restoring session storage.';
          return;
        }
        this.closeClinicSettingsModal();
        this.clinicActionSuccessText = `${this.clinicLabel} settings saved for "${clinic.clinicName}"`;
        // Never overwrite current state with a historical replay receipt.
        this.loadClinicSettings();
      },
      error: (error: unknown) => {
        this.isSavingClinicSettings = false;
        // A rejected first request has no earlier ambiguous attempt with this id.
        if (firstAttempt && [400, 403, 404].includes((error as { status?: number })?.status ?? 0)) {
          try {
            this.clinicSaveDrafts.clear(clinic.clinicId, companyId);
            this.pendingClinicSave = null;
            this.clinicSaveError = resolveHttpErrorMessageKey(error, {
              badRequestByMessage: {
                'workingDayEnd must be greater than workingDayStart': 'Working day end must be later than start. Use 00:00 for midnight.',
              },
              fallback: 'Save was rejected. Review the settings and assigned procedures.',
            });
          } catch {
            this.clinicSaveError = 'Recovery record could not be cleared. Restore session storage and check the result.';
          }
          return;
        }
        this.clinicSaveError = 'Save was not confirmed. Check its result or retry the same save.';
      },
    });
  }

  private loadOperations(): void
  {
    this.operationsState.load(this.getTargetCompanyId());
  }

  protected loadClinicSettings(): void
  {
    this.clinicListRequest?.unsubscribe();
    this.isClinicSettingsLoading = true;
    this.clinicSettingsErrorText = null;

    this.clinicListRequest = this.currentSettingsRequest(this.settingsService.getClinicOperationalSettings(this.getTargetCompanyId())).subscribe({
      next: (settings: ClinicOperationalSettingsDto[]) =>
      {
        this.clinicSettings = settings;
        this.applyClinicListFilters();
        this.isClinicSettingsLoading = false;
      },
      error: (error: unknown) =>
      {
        this.clinicSettingsErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
        this.visibleClinicSettings = [];
        this.isClinicSettingsLoading = false;
      },
    });
  }

  public ngOnDestroy(): void {
    this.disposeSettings();
  }

  private normalizeLabel(value: string | null | undefined, fallback: string): string {
    const normalized = (value ?? '').trim().replace(/\s+/g, ' ');
    return normalized || fallback;
  }

  private applyClinicListFilters(): void {
    const query = this.appliedClinicFilters.query.trim().toLowerCase();
    const filtered = this.clinicSettings.filter((clinic) => {
      if (query) {
        const haystack = [
          clinic.clinicId,
          clinic.clinicName,
          clinic.city ?? '',
          clinic.address ?? '',
          clinic.timeZone,
          clinic.timeZoneInherited ?? '',
        ].join(' ').toLowerCase();
        if (!haystack.includes(query)) {
          return false;
        }
      }

      if (this.appliedClinicFilters.timeZoneMode === 'inherit' && clinic.timeZoneSource === 'clinic_override') {
        return false;
      }
      if (this.appliedClinicFilters.timeZoneMode === 'override' && clinic.timeZoneSource !== 'clinic_override') {
        return false;
      }

      const hasSlotOverride = (clinic.slotDurationMinutesOverride ?? 0) > 0;
      if (this.appliedClinicFilters.slotOverride === 'inherit' && hasSlotOverride) {
        return false;
      }
      if (this.appliedClinicFilters.slotOverride === 'override' && !hasSlotOverride) {
        return false;
      }

      return true;
    });

    this.visibleClinicSettings = [...filtered].sort((left, right) => this.compareClinics(left, right));
  }

  private compareClinics(
    left: ClinicOperationalSettingsDto,
    right: ClinicOperationalSettingsDto,
  ): number {
    const compareText = (a: string | null | undefined, b: string | null | undefined) =>
      (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base' });

    switch (this.appliedClinicFilters.sort) {
      case 'nameDesc':
        return compareText(right.clinicName, left.clinicName);
      case 'cityAsc':
        return compareText(left.city, right.city) || compareText(left.clinicName, right.clinicName);
      case 'cityDesc':
        return compareText(right.city, left.city) || compareText(left.clinicName, right.clinicName);
      case 'workingDayAsc':
        return compareText(left.workingDayStart, right.workingDayStart) || compareText(left.clinicName, right.clinicName);
      case 'workingDayDesc':
        return compareText(right.workingDayStart, left.workingDayStart) || compareText(left.clinicName, right.clinicName);
      case 'nameAsc':
      default:
        return compareText(left.clinicName, right.clinicName);
    }
  }

  private lowerFirst(value: string): string {
    return value ? value.charAt(0).toLowerCase() + value.slice(1) : value;
  }

  private toPlural(value: string): string {
    if (!value) {
      return value;
    }

    const lower = value.toLowerCase();
    if (lower.endsWith('s')) {
      return value;
    }

    return `${value}s`;
  }

  protected isCompanyContextRequiredError(value: string | null | undefined): boolean {
    return value === ClinicSettingsPanelComponent.COMPANY_CONTEXT_REQUIRED_ERROR_KEY;
  }
}
